import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { useMemo } from 'react'
import { store, useAppSelector } from '../root'

export type Feature = 'table-change-highlight'

export interface FeatureState {
  enabledFeatures: Feature[]
  disabledFeatures: Feature[]
}

const initialFeatureState: FeatureState = {
  enabledFeatures: [],
  disabledFeatures: ['table-change-highlight'],
}

export const isFeatureEnabled = (state: FeatureState, feature: Feature) =>
  state.enabledFeatures.includes(feature) && !state.disabledFeatures.includes(feature)

export const featureSlice = createSlice({
  name: 'feature-slice',
  initialState: initialFeatureState,
  reducers: {
    enableFeature: (state, { payload: feature }: PayloadAction<Feature>) => {
      state.disabledFeatures = state.disabledFeatures.filter((x) => x !== feature)
      state.enabledFeatures = [...state.enabledFeatures.filter((x) => x !== feature), feature]
    },
    disableFeature: (state, { payload: feature }: PayloadAction<Feature>) => {
      state.enabledFeatures = state.enabledFeatures.filter((x) => x !== feature)
      state.disabledFeatures = [...state.disabledFeatures.filter((x) => x !== feature), feature]
    },
    resetFeature: (state, { payload: feature }: PayloadAction<Feature>) => {
      state.enabledFeatures = state.enabledFeatures.filter((x) => x !== feature)
      state.disabledFeatures = state.disabledFeatures.filter((x) => x !== feature)
    },
    toggleFeature: (state, { payload: feature }: PayloadAction<Feature>) => {
      const enabled = isFeatureEnabled(state, feature)
      if (!enabled) {
        state.disabledFeatures = state.disabledFeatures.filter((x) => x !== feature)
        state.enabledFeatures = [...state.enabledFeatures.filter((x) => x !== feature), feature]
      } else {
        state.enabledFeatures = state.enabledFeatures.filter((x) => x !== feature)
        state.disabledFeatures = [...state.disabledFeatures.filter((x) => x !== feature), feature]
      }
    },
  },
})

// @ts-expect-error (we're poorly adding a hook to flip a toggle when showing features)
window.toggleChangeHighlight = () => store.dispatch(featureSlice.actions.toggleFeature('table-change-highlight'))
export const useTableChangeHighlightEnabled = () => {
  const state = useAppSelector((x) => x.features)
  return useMemo(() => isFeatureEnabled(state, 'table-change-highlight'), [state])
}

export const { enableFeature, disableFeature, resetFeature, toggleFeature } = featureSlice.actions
export const featureSliceActions = featureSlice.actions
